import { Button, LoadSpinner } from '@calizahq/react-components-v2'
import styled from '@emotion/styled'
import { useDebounce } from '@shared/hooks'
import { Row } from '@tanstack/react-table'
import React, { ChangeEvent, useState } from 'react'

import { Dropdown } from '@/components'
import { useAlertContext, useUserContext } from '@/context'
import { TicketMutations } from '@/graphql/mutations/ticket.mutations'
import { UserQueries } from '@/graphql/queries/user.queries'
import { AccessLevel, useAccessLevel } from '@/hooks/useAccessLevel'
import { useGraphql, useGraphqlMutation } from '@/hooks/useGraphql'
import useTickets from '@/hooks/useTickets/useTickets'

type userOptions = {
  fullName?: string
  id: number
}

type Props = {
  rowSelected: Row<any>[]
}

export const AssignFilter = ({ rowSelected }: Props) => {
  const [dropdownExpanded, setDropdownExpanded] = useState(false)
  const { refetch } = useTickets()
  const [searchString, setSearchString] = useState<string>()
  const { accessGranted } = useAccessLevel()
  const { AGM, HUB_MANAGER } = AccessLevel
  const debouncedSearch = useDebounce(searchString, 1000)
  const { setAlert } = useAlertContext()
  const { user } = useUserContext()
  const { mutate } = useGraphqlMutation(TicketMutations.assignTickets(), {
    onSuccess: () => {
      refetch()
      setAlert('Ticket owner updated', 'success')
    },
  })
  const { data, isLoading } = useGraphql<{ authorizedUsers: userOptions[] }>(
    UserQueries.QUERY_KEYS.getSearchOwner,
    UserQueries.getSearchOwner(),
    {
      searchString: debouncedSearch,
    },
  )

  const assignTicket = (id?: number) => {
    const selectedTicketIDs = rowSelected.map((d) => d.original.id)

    setDropdownExpanded(false)
    if (selectedTicketIDs && selectedTicketIDs.length && id) {
      mutate({
        userId: id,
        ticketIds: selectedTicketIDs,
      })
    }
  }

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()
    setSearchString(e.target.value)
  }

  return (
    <>
      {accessGranted([HUB_MANAGER]) ? (
        <Dropdown
          className="mr-4 hidden w-full md:w-auto lg:flex"
          expanded={dropdownExpanded}
          headerTitle="Assign Ticket"
          onHideDropdown={() => setDropdownExpanded(false)}
          renderHeader={() => <div></div>}
          setExpanded={setDropdownExpanded}
        >
          <AssignFilterOptionsStyled>
            <div className="flex w-full px-4">
              <input
                autoComplete="off"
                className="mb-2  w-full rounded border-2 border-border-secondary p-2 focus:outline-none"
                onChange={handleSearch}
                placeholder="Search"
                value={searchString || ''}
              />
            </div>
            <div className="flex w-full">
              {isLoading ? (
                <div className="flex h-24 w-full items-center justify-center">
                  <LoadSpinner className="icon-secondary" isCurrentColor={true} size={2} />
                </div>
              ) : (
                <div className="flex max-h-48 min-h-24 w-full overflow-scroll">
                  {data && data?.authorizedUsers?.length && (
                    <ul>
                      {data?.authorizedUsers.map((authorizedUser) => (
                        <li
                          key={authorizedUser.id}
                          onClick={() => assignTicket(authorizedUser.id)}
                          role={'presentation'}
                        >
                          {authorizedUser.fullName}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </AssignFilterOptionsStyled>
        </Dropdown>
      ) : (
        <>
          {accessGranted([AGM]) && (
            <Button
              className="mr-4 bg-action-secondary"
              onClick={() => assignTicket(user?.id)}
              size="small"
              variant="secondary"
            >
              Assign ticket to me
            </Button>
          )}
        </>
      )}
    </>
  )
}

export const AssignFilterOptionsStyled = styled.div`
  width: 13rem;
  display: flex;
  flex-direction: column;
  padding-top: 1rem;
  padding-bottom: 1rem;

  ul {
    width: 100%;
    padding-top: 0.5rem;
    padding-bottom: 0.25rem;
  }
  li {
    margin-bottom: 0.25rem;
    padding-left: 1rem;
    padding-right: 1rem;
    width: 100%;
    cursor: pointer;
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
    line-height: 1;
    &:hover {
      background-color: #f3f3f2;
    }
  }
`
